import * as React from "react"

import { cn } from "./cn"

/**
 * A data table: a scrolling wrapper and plain table elements carrying the
 * look. In the AVEX design the head is the design system's DataTable head —
 * small capitals on the sunken surface — and rows are separated by a hairline;
 * in the classic look the grey uppercase head and the light dividers modules
 * drew by hand before. Sorting, selection and the rest stay with the module.
 *
 * `maxHeight` gives a long list a box of its own: the body scrolls, the head
 * stays visible. Without it the table grows with its rows, as before.
 */
function Table({
  className,
  maxHeight,
  ...props
}: React.ComponentProps<"table"> & {
  /**
   * The tallest the table may get, as a CSS length ("360px", "50vh"). The body
   * then scrolls inside the kit's own wrapper and the head stays at its top.
   * The wrapper has to be the scroll box for that: a sticky head sticks to its
   * nearest scrolling ancestor, and a box drawn around the table never is one.
   */
  maxHeight?: string
}) {
  return (
    <div
      data-slot="table"
      data-scroll={maxHeight === undefined ? undefined : "y"}
      style={maxHeight === undefined ? undefined : { maxHeight }}
      className={cn(
        "w-full overflow-x-auto",
        maxHeight !== undefined && [
          "overflow-y-auto",
          // The head on its own background, so rows never show through it, and
          // with its divider drawn as a shadow: a collapsed border stays behind.
          "[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-white [&_th]:shadow-[inset_0_-1px_0_theme(colors.gray.100)]",
          "avex:[&_th]:bg-surface-sunken avex:[&_th]:shadow-none"
        ]
      )}
    >
      <table className={cn("w-full border-collapse text-sm", className)} {...props} />
    </div>
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead className={cn("[&>tr]:border-b [&>tr]:border-gray-100 avex:[&>tr]:border-0", className)} {...props} />
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return <tbody className={cn("divide-y divide-gray-50 avex:divide-y-0", className)} {...props} />
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return <tr className={cn("avex:transition-colors avex:hover:bg-surface-muted", className)} {...props} />
}

type CellProps = {
  /** Right-aligned with tabular figures, for amounts and counts. */
  numeric?: boolean
}

function TableHeaderCell({ className, numeric, ...props }: React.ComponentProps<"th"> & CellProps) {
  return (
    <th
      className={cn(
        "py-2 pr-4 text-left text-xs font-medium uppercase text-gray-400",
        "avex:h-10 avex:bg-surface-sunken avex:px-4 avex:py-0 avex:text-[0.6875rem] avex:font-semibold avex:tracking-[0.06em] avex:whitespace-nowrap avex:text-ink-muted",
        "avex:first:rounded-l-avex-sm avex:last:rounded-r-avex-sm",
        numeric && "text-right tabular-nums",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, numeric, ...props }: React.ComponentProps<"td"> & CellProps) {
  return (
    <td
      className={cn(
        "py-2 pr-4 align-top",
        "avex:border-t avex:border-line avex:px-4 avex:py-2.5",
        numeric && "text-right tabular-nums",
        className
      )}
      {...props}
    />
  )
}

export { Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell }
